import { Component, Input, Output, EventEmitter, signal, ContentChildren, QueryList, AfterContentInit, inject, SimpleChanges, effect, OnInit, OnDestroy, TemplateRef, ViewChild, computed } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItemObject } from '../../models/context-menu.models';
import { PaginatorComponent } from '../../nested-components/paginator/paginator.component';
import { SearchBarComponent } from '../../nested-components/search-bar/search-bar.component';
import { TableComponent } from '../list-table/list-table.component';
import { ListTableColumnComponent } from '../list-table-column/list-table-column.component';
import { IEntityMetaData, IMetaProprety, IPropretyAttribute } from '../../../_models/IEntityMetaData';
import { CommonModule } from '@angular/common';
import { ColumnType } from '../../models/components.models';
import { Decorators } from '../../../decorators/EntityDecorators';
import { EntityServiceProvider } from '../../../services/DataServiceProvider';
import { EntityConstructor } from '../../../services/DependecyInjection.provider';
import { EntityBase } from '../../../_models/EntityBase';
import { GenericEntityBase } from '../../../_models/GenericEntityBase';
import { ButtonPaginatorComponent } from '../../nested-components/button-paginator/button-paginator.component';
import { TranslationService } from '../../../services/TranslationService';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { DateRangeSelectComponent } from '../../nested-components/date-range-select/date-range-select.component';
import { DateRangePickerComponent } from '../../nested-components/date-range-picker/date-range-picker.component';
import { IListType } from '../../_primitives/IListType';
import { DateRangePreset, MatchPreset } from '../../../helpers/dateRangeHelpers';
import { ListStackComponent } from '../list-stack/list-stack.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RichTooltipDirective } from '../../directives/Tooltip.directive';
import { EntityState, IEntityState } from '../../../_models/IChangedEntity';

export interface IPagination {
  enabled: boolean,
  pageLengths: number[],
  defaultPageLength: number,
  searchColumns: string[] | null;
  sortableColumns: string[] | null;

};

export interface IDateSelected {
  startDate: Date;
  endDate?: Date;
  // Plage nommee en vigueur. Seul ce choix est conserve : les bornes se recalculent a partir
  // de lui, pour qu' "Aujourd'hui" reste aujourd'hui apres une restauration.
  preset?: DateRangePreset;
}

@Component({
  selector: 'app-list-view',
  templateUrl: './list-view.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SearchBarComponent,
    PaginatorComponent,
    ButtonPaginatorComponent,
    TableComponent,
    ListStackComponent,
    TranslatePipe,
    DateRangeSelectComponent,
    DateRangePickerComponent,
    //RichTooltipDirective
  ]
})
export class ListViewComponent<T extends EntityBase & IEntityState> implements AfterContentInit, OnInit, OnDestroy {

  public metaData!: IEntityMetaData;
  protected entityServiceProvider = inject(EntityServiceProvider);

  @Input() entityType!: EntityConstructor<T>;
  @Input() title = '';
  @Input() addButtonLink: string | undefined = '';
  @Input() contextMenuItems: MenuItemObject<T>[] = [];
  @Input() parentEntity?: EntityBase; // Pour les listes imbriquées

  @ViewChild('entityStateTooltip', { static: true }) entityStateTooltip!: TemplateRef<any>;
  @Input() dismissEntity?: (entity: any) => void;

  @Input() paginatorType: 'dropdown' | 'button' = 'dropdown';
  @Input() showHeader: boolean = true;
  @Input() showSearchBar: boolean = true;
  @Input() listType: IListType = IListType.Table;
  public IListType = IListType; // Make IListType available in the template

  @Input() showCreateButton: boolean = true;
  @Input() createDisabled: boolean = false;
  @Output() createButtonClick = new EventEmitter<void>();

  @Input() showActions: boolean = true;
  @Input() canEdit: (item: T) => boolean = () => true;
  @Output() editAction = new EventEmitter<T>();
  @Output() deleteAction = new EventEmitter<T>();

  @Input() showEntityState: boolean = true; // affichage de l'état de Entity Suite au push-back WebSocketS : activé par défaut
  @Input() ShowRangeDateFilter: boolean = false;

  // Fuseau IANA par site (id -> IANA), aux sites dont le decalage differe du lecteur.
  @Input() fuseauxParSite: Map<number, string> = new Map();

  @Input() onDismissAdded?: (entity: T) => void;
  @Input() onRowDismiss?: (entity: T) => void;

  selectedStartDate: Date = new Date();
  selectedEndDate: Date = new Date();

  defaultPageSize: number = 10;

  defaultDateSelected: IDateSelected = {
    startDate: new Date(),
    endDate: new Date()
  };

  private aggregatedDismissTimer: any;

  // Pagination
  @Input() pagination: IPagination = {
    enabled: true,
    pageLengths: [5, 10, 20, 50, 100],
    defaultPageLength: this.defaultPageSize,
    searchColumns: [] as string[] | null,
    sortableColumns: [] as string[] | null
  };

  public addedNotifications = signal<T[]>([]);

  // Signaux
  dateSelected = signal<IDateSelected | null>(this.defaultDateSelected);
  page = signal(1);
  pageSize = signal(this.defaultPageSize);
  searchTerm = signal('');
  sortBy = signal('');
  sortOrder = signal<'asc' | 'desc'>('asc');
  refreshCounter = signal(0);

  /** Colonnes de recherche résolues depuis les métadonnées de l'entité. */
  resolvedSearchColumns: string[] | null = null;
  /** Colonnes triables résolues depuis les métadonnées de l'entité. */
  resolvedSortableColumns: string[] | null = null;
  selectedId = signal<number | string | null>(null);
  // loadedCount : ce que la liste detient, a comparer au total, qui vient de la base. Un
  // ecart signale que le resultat affiche est partiel.
  entities = signal<{ items: T[], total: number, truncated?: boolean, loadedCount?: number }>({ items: [], total: 0 });
  columns = signal<ListTableColumnComponent[]>([]);
  searchState = signal<'idle' | 'typing' | 'searching'>('idle');
  isLargeScreen = signal<boolean>(false);
  private destroy$ = new Subject<void>();

  // Outputs
  @Output() entitySelected = new EventEmitter<number | string | null>();
  @Output() contextMenuAction = new EventEmitter<{ action: string, entity: T }>();

  // Références aux composants
  @ContentChildren(ListTableColumnComponent) columnComponents!: QueryList<ListTableColumnComponent>;

  hasAnyNotification = computed(() => {
    const { added, modified, deleted } = this.notificationSummary();
    return added + modified + deleted > 0;
  })

  // Nombre de lignes « ajoutees » affichees — lu par le minuteur de refermeture.
  addedCount = computed(() => this.notificationSummary().added);
  constructor(private translationService: TranslationService, private breakpointObserver: BreakpointObserver) {
    // Bandeau : refermeture 10 s apres le dernier ajout (compteur numerique, pour ne pas relancer le delai).
    effect(() => {
      if (this.addedCount() > 0) this.startAggregatedDismissTimer();
      else this.cancelAggregatedDismissTimer();
    });
  }

  ngOnInit() {
    // BreakpointObserver subscription
    this.breakpointObserver.observe(['(min-width: 850px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isLargeScreen.set(result.matches);
      });

    if (!this.entityType) return;
    this.metaData = this.entityType.getMetaData();


    let sortFullColumns = GenericEntityBase.GetPropretiesByDecoratorFromMeta(this.metaData, IPropretyAttribute.SortColumn);
    let filterFullColumns = GenericEntityBase.GetPropretiesByDecoratorFromMeta(this.metaData, IPropretyAttribute.FilterColumn);

    this.pageSize.set(this.pagination.defaultPageLength);

    // Colonnes deduites des metadonnees, conservees ici : l'objet `pagination` vient d'un
    // litteral du template, recree a chaque cycle, ce qui ecraserait toute valeur ecrite.
    this.resolvedSearchColumns = this.pagination.searchColumns?.length
      ? this.pagination.searchColumns
      : filterFullColumns.map((metaProp: IMetaProprety) => metaProp.PropretyName);
    this.resolvedSortableColumns = this.pagination.sortableColumns?.length
      ? this.pagination.sortableColumns
      : sortFullColumns.map((metaProp: IMetaProprety) => metaProp.PropretyName);
    if (!this.sortBy() && sortFullColumns.length > 0) {
      this.sortBy.set(sortFullColumns[0].PropretyName);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelAggregatedDismissTimer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columnComponents']) {
      this.updateColumns();
    }
  }

  ngAfterContentInit() {
    this.columnComponents.changes.subscribe(() => {
      this.updateColumns();
    });
    this.updateColumns();
  }

  private updateColumns() {
    const columnsArray = this.columnComponents.filter((col) => { return col.visible && col.visible.toLowerCase() !== 'false'; });  // on filtre les colonnes visibles seulement
    this.columns.set(columnsArray);

    // Si aucune colonne n'est fournie, générer automatiquement
    if (!columnsArray.length) {
      this.generateColumnsFromMetadata();
    }
  }

  private generateColumnsFromMetadata() {
    const generatedColumns: ListTableColumnComponent[] = [];

    this.metaData.PropertyNames.forEach(prop => {
      const column = new ListTableColumnComponent();
      column.key = prop;
      column.title = this.formatTitle(prop);
      column.type = this.determineColumnType(prop);

      const propDescriptor = this.metaData.PropretyDescriptors[prop];
      // Vérifier si la propriété a l'attribut SortColumn
      column.sortable = propDescriptor?.PropretyAttributes?.includes(IPropretyAttribute.SortColumn) ?? false;

      generatedColumns.push(column);
    });

    this.columns.set(generatedColumns);
  }

  private formatTitle(key: string): string {
    // CORRECTION: Utiliser DisplayName s'il existe, sinon formater le nom
    const propDescriptor = this.metaData.PropretyDescriptors[key];
    if (propDescriptor?.PropretyDisplayName) {
      return propDescriptor.PropretyDisplayName;
    }

    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private determineColumnType(key: string): ColumnType {
    const propDescriptor = this.metaData.PropretyDescriptors[key];

    // type basé sur PropretyType ou sur le nom
    if (propDescriptor?.PropretyType) {
      const propType = propDescriptor.PropretyType;
      if (propType.includes('Integer') || propType.includes('Decimal')) {
        return 'number';
      }
    }

    // Fallback 
    try {
      // Essayer d'utiliser la reflexion TypeScript si disponible
      const instance = new this.entityType();
      const designType = Reflect.getMetadata('design:type', instance, key);

      if (designType === Date) return 'date';
      if (designType === Boolean) return 'boolean';
      if (key.toLowerCase().includes('progress')) return 'progress';
    } catch (error) {
      // Si la reflexion echoue, utiliser une logique de fallback
      if (key.toLowerCase().includes('date')) return 'date';
      if (key.toLowerCase().includes('is') || key.toLowerCase().includes('has')) return 'boolean';
    }

    return 'text';
  }

  refresh() {
    this.refreshCounter.update(v => v + 1);
  }

  sort(column: string) {
    if (this.pagination.sortableColumns?.includes(column)) {
      if (this.sortBy() === column) {
        this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
      } else {
        this.sortBy.set(column);
        this.sortOrder.set('asc');
      }
      this.page.set(1); // un nouveau tri repart de la premiere page
      this.refresh();
    }
  }

  onTableSortChange(event: { sortBy: string, sortOrder: 'asc' | 'desc' }) {
    this.sortBy.set(event.sortBy);
    this.sortOrder.set(event.sortOrder);
    this.page.set(1); // un nouveau tri repart de la premiere page
    this.refresh();
  }

  onTableEntitySelected(item: T | number | string | null) {
    let selectedId: number | string | null = null;
    let tyofitem = typeof item;
    if (typeof item === 'number' || typeof item === 'string' || item === null) {
      selectedId = item;
    } else if (typeof item === 'object' && item && 'id' in item) {
      selectedId = (item as any).id;
    }
    this.selectedId.set(selectedId);
    this.entitySelected.emit(selectedId);
  }

  onTableContextMenuAction(event: { action: string, entity: T }) {
    this.contextMenuAction.emit(event);
  }

  onSearch(term: string) {
    // La croix native d'un input[type=search] peut emettre l'evenement DOM au lieu
    // d'une chaine : le signal ne stocke que du texte, sinon .trim() explose en aval.
    this.searchTerm.set(typeof term === 'string' ? term : '');
    this.page.set(1); // reset à la première page
    this.refresh();
  }

  getPagination(): IPagination {
    // Restitue les colonnes résolues (recherche/tri) absentes de l'objet d'entrée
    return {
      ...this.pagination,
      searchColumns: this.resolvedSearchColumns,
      sortableColumns: this.resolvedSortableColumns,
    };
  }

  onEditAction(entity: T) {
    this.editAction.emit(entity);
  }

  onDeleteAction(entity: T) {
    this.deleteAction.emit(entity);
  }

  onDateRangeChange(event: { startDate: Date; endDate: Date; preset?: DateRangePreset }): void {
    this.selectedStartDate = event.startDate;
    this.selectedEndDate = event.endDate;
    this.dateSelected.set({ startDate: event.startDate, endDate: event.endDate, preset: event.preset });
    this.refresh();
  }

  // Une plage choisie au calendrier ne correspond a aucune plage nommee : le selecteur bascule
  // alors sur "Periode libre".
  onPickerRangeChange(event: { startDate: Date; endDate: Date }): void {
    this.onDateRangeChange({ ...event, preset: MatchPreset(event.startDate, event.endDate) });
  }

  onCreateButtonClick() {
    this.createButtonClick.emit();
  }

  dismissAdded(entity: T): void {
    this.addedNotifications.update(list => list.filter(e => e.id !== entity.id));
  }

  getEntityColumnsData(entity: T): { label: string, value: any }[] {
    return this.columns()
      .filter(col => col.visible !== 'false' && ['text', 'boolean', 'date', 'datetime', 'number'].includes(col.type))
      .map(col => ({
        label: col.title,
        value: this.getNestedValue(entity, col.key || '') ?? '—'
      }));
  }

  getTooltipContextForNotification(notif: T): { entity: T, dismiss: () => void } {
    return {
      entity: notif,
      dismiss: () => this.dismissAdded(notif)
    };
  }
  private getNestedValue(obj: any, key: string): any {
    return key.split('.').reduce((o, k) => {
      if (o == null) return undefined;
      // Gère la notation avec crochets : prop[0] → prop puis index 0
      const match = k.match(/^(.+)\[(\d+)\]$/);
      if (match) {
        const arr = o[match[1]];
        const idx = parseInt(match[2], 10);
        return arr && arr[idx] !== undefined ? arr[idx] : undefined;
      }
      return o[k] !== undefined ? o[k] : undefined;
    }, obj);
  }

  private resetSingleEntity(entity: T): T {
    return {
      ...entity,
      _entityState: EntityState.Unchanged,
      _modifiedProperties: new Set<string>()
    };
  }

  private dismissNotifications(filter?: (state: EntityState) => boolean): void {
    const currentItems = this.entities().items;
    let hasChanges = false;

    const updatedItems = currentItems.map(item => {
      if ((!filter || filter(item._entityState)) && item._entityState !== EntityState.Unchanged) {
        hasChanges = true;
        return this.resetSingleEntity(item);
      }
      return item;
    });

    if (hasChanges) {
      // Créer un nouvel objet pour le signal (immuabilité)
      this.entities.set({ items: updatedItems, total: this.entities().total });
    }

    // Vider le signal des ajouts
    if (this.addedNotifications().length > 0) {
      this.addedNotifications.set([]);
    }
  }



  dismissAllAdded(): void {
    this.dismissNotifications(state => state === EntityState.Added);
  }

  dismissAllNotifications(): void {
    this.dismissNotifications();
  }


  // Compte les entités par état de notification
  notificationSummary = computed(() => {
    const items = this.entities().items;
    let added = 0;
    let modified = 0;
    let deleted = 0;
    for (const item of items) {
      if (item._entityState === EntityState.Added) added++;
      else if (item._entityState === EntityState.Modified) modified++;
      else if (item._entityState === EntityState.Deleted) deleted++;
    }
    return { added, modified, deleted };
  });


  private haveSameOrigin(notifications: T[]): boolean {
    if (notifications.length === 0) return false;

    const first = notifications[0];
    const firstBy = first._modifiedBy;
    const firstAt = first._modifiedAt;

    // Si le premier n'a aucune info, on ne peut pas déclarer une origine commune fiable
    if (!firstBy && !firstAt) return false;

    return notifications.every(n =>
      n._modifiedBy === firstBy &&
      this.isSameTimestamp(n._modifiedAt, firstAt)
    );
  }

  private isSameTimestamp(a?: Date, b?: Date): boolean {
    if (!a || !b) return false;
    return new Date(a).getTime() === new Date(b).getTime();
  }


  getAggregatedTooltipContext(): { entity: any, dismiss: () => void } {
    const notifications = this.addedNotifications();
    const sameOrigin = this.haveSameOrigin(notifications);

    const representative: any = {
      _entityState: EntityState.Added,
      _modifiedProperties: new Set<string>(),
      id: 'aggregated'
    };

    if (sameOrigin) {
      representative._modifiedBy = notifications[0]._modifiedBy;
      representative._modifiedAt = notifications[0]._modifiedAt;
    }
    // Si sameOrigin === false : _modifiedBy et _modifiedAt restent undefined
    //  le template ne rendra pas la section date/auteur

    return {
      entity: representative,
      dismiss: () => this.dismissAllAdded()
    };
  }


  startAggregatedDismissTimer(): void {
    this.cancelAggregatedDismissTimer();
    this.aggregatedDismissTimer = setTimeout(() => {
      this.dismissAllAdded();
    }, 10000);
  }

  cancelAggregatedDismissTimer(): void {
    if (this.aggregatedDismissTimer) {
      clearTimeout(this.aggregatedDismissTimer);
      this.aggregatedDismissTimer = null;
    }
  }




}
